// The framed mode of this page. IAP's session refresher loads it in a hidden
// iframe on the Lernwelt host (ADR-0020): gcip-iap renews the session from
// the stored sign-in, and the learner sees nothing. When that is not
// possible, nobody can act on a form in a hidden frame, and the Lernwelt
// would wait for an outcome that never comes. So a framed page shows no form
// and tells the embedding Lernwelt what happened instead.

/** True when another document embeds this page. */
export function isFramed(w: Window = window): boolean {
  return w.self !== w.top;
}

/**
 * What the framed page tells the Lernwelt when the silent renewal did not
 * happen. The Lernwelt mirrors this type (TMIG-817) and accepts a message
 * only from the iframe it created and only with this `source`.
 *
 * - `sign-in-required`: gcip-iap has no usable stored sign-in; the learner
 *   has to sign in on the full page. With `reason: "account-disabled"` the
 *   stored sign-in belongs to an account that is disabled: signing in again
 *   cannot help, and the Lernwelt blocks the page instead (ADR-0020,
 *   decision point 5).
 * - `session-refresh-failed`: anything else, with gcip-iap's `code` and
 *   `message` where it gave them.
 */
export type FramedReport = {
  source: "lernwelt-login";
  type: "sign-in-required" | "session-refresh-failed";
  reason?: "account-disabled";
  code?: string;
  message?: string;
};

/**
 * Sends the report to the embedding window, addressed to the origin of the
 * tenant's app URL (config/tenants.json) and to no other: a page on any
 * other origin never receives it.
 */
export function reportToParent(
  report: FramedReport,
  appUrl: string,
  w: Window = window,
): void {
  w.parent.postMessage(report, new URL(appUrl).origin);
}
